import React from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';

// Premium styling for the contract PDF
const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontSize: 9,
    fontFamily: 'Helvetica',
    color: '#1e293b',
    backgroundColor: '#ffffff',
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 20,
    borderBottomWidth: 2,
    borderBottomColor: '#2563eb', // Optimox blue accent
    paddingBottom: 12,
  },
  titleSection: {
    flexDirection: 'column',
  },
  docTitle: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#1e3a8a', // Dark blue
    marginBottom: 2,
  },
  docSub: {
    fontSize: 8,
    color: '#2563eb',
    textTransform: 'uppercase',
    letterSpacing: 1.5,
    fontWeight: 'bold',
  },
  metaSection: {
    textAlign: 'right',
    fontSize: 8,
    lineHeight: 1.4,
    color: '#475569',
  },
  // Address & Details Container
  infoContainer: {
    flexDirection: 'row',
    gap: 15,
    marginBottom: 15,
  },
  infoCard: {
    flex: 1,
    backgroundColor: '#f8fafc',
    borderWidth: 1,
    borderColor: '#e2e8f0',
    borderRadius: 8,
    padding: 12,
  },
  cardTitle: {
    fontSize: 7,
    textTransform: 'uppercase',
    color: '#2563eb',
    fontWeight: 'bold',
    marginBottom: 6,
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
    paddingBottom: 3,
  },
  cardText: {
    fontSize: 8,
    lineHeight: 1.4,
    color: '#1e293b',
  },
  cardTextBold: {
    fontWeight: 'bold',
    color: '#0f172a',
  },
  // Table
  table: {
    marginTop: 10,
    width: '100%',
    borderWidth: 1,
    borderColor: '#e2e8f0',
    borderRadius: 8,
    overflow: 'hidden',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#f1f5f9',
    borderBottomWidth: 1,
    borderBottomColor: '#cbd5e1',
    padding: 8,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#f1f5f9',
    padding: 8,
    alignItems: 'center',
  },
  tableColDesc: { flex: 3.5 },
  tableColQty: { flex: 0.8, textAlign: 'center' },
  tableColPrice: { flex: 1.2, textAlign: 'right' },
  tableColVat: { flex: 0.8, textAlign: 'right' },
  tableColTotal: { flex: 1.5, textAlign: 'right' },
  headerText: {
    fontSize: 8,
    fontWeight: 'bold',
    color: '#475569',
    textTransform: 'uppercase',
  },
  rowText: {
    fontSize: 8,
    color: '#334155',
  },
  rowTextBold: {
    fontWeight: 'bold',
    color: '#0f172a',
  },
  // Totals
  totalsContainer: {
    marginTop: 12,
    flexDirection: 'row',
    justifyContent: 'flex-end',
  },
  totalsBox: {
    width: '40%',
    backgroundColor: '#f8fafc',
    borderWidth: 1,
    borderColor: '#e2e8f0',
    borderRadius: 8,
    padding: 10,
  },
  totalsRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    paddingVertical: 3,
    fontSize: 8,
  },
  totalsRowBold: {
    fontSize: 10,
    fontWeight: 'bold',
    color: '#1e3a8a',
    borderTopWidth: 1.5,
    borderTopColor: '#2563eb',
    marginTop: 4,
    paddingTop: 4,
  },
  // Condition Box
  conditionSection: {
    marginTop: 15,
    backgroundColor: '#f8fafc',
    borderWidth: 1,
    borderColor: '#e2e8f0',
    borderRadius: 8,
    padding: 12,
  },
  conditionText: {
    fontSize: 8,
    lineHeight: 1.4,
    color: '#334155',
  },
  // Acceptance Clause
  acceptanceSection: {
    marginTop: 15,
    backgroundColor: '#eff6ff', // Soft blue legal tint
    borderWidth: 1,
    borderColor: '#bfdbfe',
    borderRadius: 8,
    padding: 12,
  },
  acceptanceTitle: {
    fontSize: 8,
    textTransform: 'uppercase',
    color: '#1e40af',
    fontWeight: 'bold',
    marginBottom: 4,
  },
  acceptanceText: {
    fontSize: 8,
    lineHeight: 1.4,
    color: '#1e3a8a',
  },
  // Signatures
  signatureSection: {
    marginTop: 30,
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  signatureBox: {
    width: '45%',
    borderTopWidth: 1.5,
    borderTopColor: '#cbd5e1',
    paddingTop: 8,
    marginTop: 35,
  },
  addressLabel: {
    fontSize: 7,
    textTransform: 'uppercase',
    color: '#475569',
    fontWeight: 'bold',
    letterSpacing: 0.5,
  },
  footer: {
    position: 'absolute',
    bottom: 25,
    left: 40,
    right: 40,
    fontSize: 6.5,
    color: '#94a3b8',
    textAlign: 'center',
    borderTopWidth: 1,
    borderTopColor: '#f1f5f9',
    paddingTop: 8,
  },
  // Terms & Conditions page styles
  termsTitle: {
    fontSize: 13,
    fontWeight: 'bold',
    color: '#1e3a8a',
    marginBottom: 8,
    borderBottomWidth: 1.5,
    borderBottomColor: '#2563eb',
    paddingBottom: 4,
  },
  termsBlock: {
    marginBottom: 10,
  },
  termsBlockHeader: {
    fontSize: 9,
    fontWeight: 'bold',
    color: '#0f172a',
    marginBottom: 4,
  },
  termsBlockText: {
    fontSize: 8,
    lineHeight: 1.4,
    color: '#4b5563',
  }
});

const getUnitLabel = (type) => {
  switch(type) {
    case 'day': return 'Tag';
    case 'hour': return 'Std';
    case 'flat': return 'psch';
    default: return type;
  }
};

const RentalPDF = ({ rental = {}, customer = {}, items = [], createdBy, contractText = {} }) => {
  const landlordName = "Optimox Solutions";
  
  // Check if there are any global or item-specific contract conditions
  const hasContractText = 
    Object.values(contractText).some(val => val) || 
    (items || []).some(i => i?.individual_contract_text);

  // Common signatures block
  const renderSignatures = () => (
    <View style={styles.signatureSection} wrap={false}>
      <View style={styles.signatureBox}>
        <Text style={styles.addressLabel}>UNTERSCHRIFT VERMIETER</Text>
        <Text style={{ fontSize: 8.5, fontWeight: 'bold', marginTop: 4 }}>{landlordName}</Text>
        <Text style={{ fontSize: 7, color: '#64748b', marginTop: 2 }}>erstellt durch: {createdBy || 'System'}</Text>
      </View>
      <View style={styles.signatureBox}>
        <Text style={styles.addressLabel}>UNTERSCHRIFT MIETER</Text>
        <Text style={{ fontSize: 8.5, fontWeight: 'bold', marginTop: 4 }}>{customer?.company_name || 'Mieter'}</Text>
        <Text style={{ fontSize: 7, color: '#64748b', marginTop: 2 }}>{customer?.contact_person ? `z.Hd. ${customer.contact_person}` : 'Vertretungsberechtigter'}</Text>
      </View>
    </View>
  );

  return (
    <Document>
      {/* PAGE 1: The Core Contract & Details */}
      <Page size="A4" style={styles.page}>
        {/* Header */}
        <View style={styles.header}>
          <View style={styles.titleSection}>
            <Text style={styles.docSub}>Mietvertrag & Übergabeprotokoll</Text>
            <Text style={styles.docTitle}>VERTRAG #{rental?.rental_number || '---'}</Text>
          </View>
          <View style={styles.metaSection}>
            <Text style={{ fontWeight: 'bold' }}>Datum: {new Date().toLocaleDateString('de-DE')}</Text>
            <Text>Sachbearbeiter: {createdBy || 'System'}</Text>
          </View>
        </View>

        {/* Side-by-Side Vermieter & Mieter Cards */}
        <View style={styles.infoContainer}>
          <View style={styles.infoCard}>
            <Text style={styles.cardTitle}>VERMIETER</Text>
            <Text style={[styles.cardText, styles.cardTextBold]}>{landlordName}</Text>
            <Text style={[styles.cardText, { marginTop: 2, fontSize: 7.5, color: '#475569' }]}>
              {contractText.contact_data || "Optimox Zentrale\nSupport & Abrechnung\nDeutschland"}
            </Text>
          </View>
          <View style={styles.infoCard}>
            <Text style={styles.cardTitle}>MIETER / KUNDE</Text>
            <Text style={[styles.cardText, styles.cardTextBold]}>{customer?.company_name || 'Einzelkunde'}</Text>
            <Text style={styles.cardText}>{customer?.address || 'Keine Adresse hinterlegt'}</Text>
            {customer?.contact_person && (
              <Text style={[styles.cardText, { marginTop: 4, color: '#475569' }]}>z.Hd.: {customer.contact_person}</Text>
            )}
            {customer?.email && (
              <Text style={[styles.cardText, { color: '#64748b', fontSize: 7.5 }]}>E-Mail: {customer.email}</Text>
            )}
          </View>
        </View>

        {/* Mietobjekt, Zeitraum & Einsatzort */}
        <View style={[styles.infoCard, { marginBottom: 15 }]}>
          <Text style={styles.cardTitle}>MIET-OBJEKT & EINSATZDATEN</Text>
          <View style={{ flexDirection: 'row', justifyContent: 'space-between' }}>
            <View>
              <Text style={styles.cardText}>
                <Text style={styles.cardTextBold}>Mietbeginn: </Text>
                {rental?.start_date ? new Date(rental.start_date).toLocaleDateString('de-DE') : '---'}
              </Text>
              <Text style={styles.cardText}>
                <Text style={styles.cardTextBold}>Geplante Rückgabe: </Text>
                {rental?.end_date ? new Date(rental.end_date).toLocaleDateString('de-DE') : 'Unbefristet'}
              </Text>
            </View>
            <View style={{ textAlign: 'right' }}>
              <Text style={styles.cardText}>
                <Text style={styles.cardTextBold}>Einsatzort / Projekt: </Text>
                {rental?.location || 'Baustelle (laut Angabe)'}
              </Text>
              <Text style={styles.cardText}>
                <Text style={styles.cardTextBold}>Abrechnungsmodus: </Text>
                {rental?.billing_mode === 'hours' ? 'Pro Stunde' : 'Pro Tag'}
              </Text>
            </View>
          </View>
        </View>

        {/* Items Table */}
        <View style={styles.table}>
          <View style={styles.tableHeader}>
            <Text style={[styles.tableColDesc, styles.headerText]}>Mietobjekt / Position</Text>
            <Text style={[styles.tableColQty, styles.headerText]}>Menge</Text>
            <Text style={[styles.tableColPrice, styles.headerText]}>EP Netto</Text>
            <Text style={[styles.tableColVat, styles.headerText]}>MwSt.</Text>
            <Text style={[styles.tableColTotal, styles.headerText]}>Gesamt Netto</Text>
          </View>
          {(items || []).map((item, idx) => {
            const qty = parseFloat(item?.quantity) || 1;
            const price = parseFloat(item?.unit_price_net) || 0;
            const net = qty * price;
            return (
              <View key={idx} style={styles.tableRow}>
                <Text style={[styles.tableColDesc, styles.rowText, styles.rowTextBold]}>{item?.name || 'Mietobjekt'}</Text>
                <Text style={[styles.tableColQty, styles.rowText, { textAlign: 'center' }]}>{qty} ({getUnitLabel(item?.pricing_type)})</Text>
                <Text style={[styles.tableColPrice, styles.rowText]}>{price.toFixed(2)} €</Text>
                <Text style={[styles.tableColVat, styles.rowText]}>{item?.vat_rate || 19}%</Text>
                <Text style={[styles.tableColTotal, styles.rowText, styles.rowTextBold]}>{net.toFixed(2)} €</Text>
              </View>
            );
          })}
        </View>

        {/* Costs Summary & Totals */}
        <View style={styles.totalsContainer}>
          <View style={styles.totalsBox}>
            <View style={styles.totalsRow}>
              <Text style={{ color: '#475569' }}>Netto-Summe:</Text>
              <Text style={{ fontWeight: 'bold' }}>{parseFloat(rental.total_net || 0).toFixed(2)} €</Text>
            </View>
            <View style={styles.totalsRow}>
              <Text style={{ color: '#475569' }}>zzgl. MwSt.:</Text>
              <Text style={{ fontWeight: 'bold' }}>{parseFloat(rental.total_vat || 0).toFixed(2)} €</Text>
            </View>
            <View style={[styles.totalsRow, styles.totalsRowBold]}>
              <Text>GESAMTBETRAG:</Text>
              <Text>{parseFloat(rental.total_brutto || 0).toFixed(2)} €</Text>
            </View>
          </View>
        </View>

        {/* Handover & Conditions Protocol */}
        <View style={styles.conditionSection}>
          <Text style={styles.cardTitle}>ZUSTAND BEI ÜBERGABE / WEITERE VEREINBARUNGEN</Text>
          <Text style={styles.conditionText}>
            {rental.handover_condition || 'Das Mietobjekt wurde im ordnungsgemäßen, sauberen, betriebsbereiten und verkehrssicheren Zustand übergeben.'}
          </Text>
        </View>

        {/* LEGAL ACCEPTANCE & RETURN CLAUSE */}
        <View style={styles.acceptanceSection}>
          <Text style={styles.acceptanceTitle}>RECHTLICHE VEREINBARUNG ÜBER ZUSTAND & RÜCKGABE</Text>
          <Text style={styles.acceptanceText}>
            Der Mieter übernimmt das Mietobjekt in dem vom Vermieter angegebenen Zustand (wie oben beschrieben) und verpflichtet sich, dieses in demselben betriebsbereiten, sauberen und vollgetankten Zustand wieder an den Vermieter zurückzugeben. Abweichungen, Mängel oder Beschädigungen sind dem Vermieter unverzüglich bei der Übergabe schriftlich anzuzeigen.
          </Text>
        </View>

        {/* Render signatures on Page 1 ONLY if there are no subsequent pages */}
        {!hasContractText && renderSignatures()}

        <Text style={styles.footer}>
          Dieses Dokument wurde digital durch das Optimox-Manager Vermietungsmodul erstellt.
          Die Preise verstehen sich rein netto zzgl. der jeweils ausgewiesenen gesetzlichen Mehrwertsteuer.
        </Text>
      </Page>
      
      {/* PAGE 2 (And 3): General Terms, Contacts & Item-Specific terms */}
      {hasContractText && (
        <Page size="A4" style={styles.page}>
          <View style={styles.header}>
            <View style={styles.titleSection}>
              <Text style={styles.docSub}>Anhang zum Mietvertrag #{rental?.rental_number || '---'}</Text>
              <Text style={styles.docTitle}>VERTRAGSKLAUSELN & BEDINGUNGEN</Text>
            </View>
          </View>
          
          <View style={{ flex: 1 }}>
            {/* Global contract text / terms */}
            {contractText.contract_text && (
              <View style={styles.termsBlock}>
                <Text style={styles.termsBlockHeader}>1. Allgemeine Mietbedingungen & Pflichten</Text>
                <Text style={styles.termsBlockText}>{contractText.contract_text}</Text>
              </View>
            )}
            
            {contractText.conditions && (
              <View style={styles.termsBlock}>
                <Text style={styles.termsBlockHeader}>2. Zahlungs- & Mietkonditionen</Text>
                <Text style={styles.termsBlockText}>{contractText.conditions}</Text>
              </View>
            )}
            
            {contractText.other_terms && (
              <View style={styles.termsBlock}>
                <Text style={styles.termsBlockHeader}>3. Sonstige Vereinbarungen</Text>
                <Text style={styles.termsBlockText}>{contractText.other_terms}</Text>
              </View>
            )}
            
            <View style={{ flexDirection: 'row', gap: 15, marginBottom: 10 }}>
              {contractText.contact_data && (
                <View style={[styles.infoCard, { flex: 1, padding: 8 }]}>
                  <Text style={[styles.cardTitle, { marginBottom: 3 }]}>Kontaktdaten Zentrale</Text>
                  <Text style={{ fontSize: 7.5, lineHeight: 1.3, color: '#4b5563' }}>{contractText.contact_data}</Text>
                </View>
              )}
              
              {contractText.emergency_contact && (
                <View style={[styles.infoCard, { flex: 1, padding: 8, borderColor: '#fca5a5' }]}>
                  <Text style={[styles.cardTitle, { color: '#dc2626', borderBottomColor: '#fee2e2', marginBottom: 3 }]}>NOTFALLKONTAKT</Text>
                  <Text style={{ fontSize: 7.5, lineHeight: 1.3, color: '#dc2626', fontWeight: 'bold' }}>{contractText.emergency_contact}</Text>
                </View>
              )}
            </View>
            
            {contractText.emergency_behavior && (
              <View style={[styles.termsBlock, { background: '#fef2f2', padding: 8, borderRadius: 6, borderWidth: 1, borderColor: '#fee2e2' }]}>
                <Text style={[styles.termsBlockHeader, { color: '#991b1b' }]}>Verhalten im Notfall / Schadenfall</Text>
                <Text style={[styles.termsBlockText, { color: '#991b1b' }]}>{contractText.emergency_behavior}</Text>
              </View>
            )}

            {/* Objektspezifische Zusatzbedingungen */}
            {(items || []).filter(item => item?.individual_contract_text).length > 0 && (
              <View style={{ marginTop: 15, borderTopWidth: 1.5, borderTopColor: '#e2e8f0', paddingTop: 10 }}>
                <Text style={[styles.termsBlockHeader, { color: '#1e3a8a', fontSize: 10 }]}>
                  Objektspezifische Zusatzbedingungen (fahrzeug-/materialbezogen)
                </Text>
                {(items || []).filter(item => item?.individual_contract_text).map((item, idx) => (
                  <View key={`individual-${idx}`} style={{ marginBottom: 8, background: '#f8fafc', padding: 8, borderRadius: 6, borderWidth: 1, borderColor: '#e2e8f0' }}>
                    <Text style={{ fontSize: 8, fontWeight: 'bold', color: '#0f172a', marginBottom: 2 }}>Für Objekt: {item.name}</Text>
                    <Text style={{ fontSize: 7.5, lineHeight: 1.4, color: '#334155' }}>{item.individual_contract_text}</Text>
                  </View>
                ))}
              </View>
            )}
          </View>

          {/* Render signatures at the absolute bottom of the entire terms page */}
          {renderSignatures()}

          <Text style={styles.footer}>
            Dieses Dokument ist rechtlich bindender Bestandteil des Mietvertrags #{rental?.rental_number || '---'}
          </Text>
        </Page>
      )}
    </Document>
  );
};

export default RentalPDF;
